import axios from '../axiosConfig'
import { getEnv } from '@utils/env'
import { SavedCard } from '@dataTypes/payment.types'
import { normalizeBrand } from '@utils/cardBrand.util'

const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_BILLING', '')

interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

export interface PaymentMethodApi {
  payment_method_id: string
  patient_id: string
  clinic_id: string
  stripe_payment_method_id: string
  type: 'card' | 'tap'
  last4: string
  brand: string
  exp_month: number
  exp_year: number
  is_default: boolean
  allow_redisplay?: boolean
  stripe_setup_intent_id?: string | null
  created_at?: string
  updated_at?: string
}

export type SavedCardDisplay = SavedCard & {
  payment_method_id?: string
  stripe_payment_method_id?: string
}

export type CreateSetupIntentResponse = ApiResponse<{
  client_secret: string
  stripe_connected_account_id: string
}>

export type ConfirmSavedPaymentMethodResponse = ApiResponse<{
  payment_method: Record<string, unknown>
}>

export type ListPaymentMethodsResponse = ApiResponse<{
  payment_methods: PaymentMethodApi[]
}>

export type DeletePaymentMethodResponse = ApiResponse<Record<string, never>>
export type SetDefaultPaymentMethodResponse = ApiResponse<Record<string, never>>

export function mapPaymentMethodApiToDisplay(
  pm: PaymentMethodApi,
): SavedCardDisplay {
  const brand = normalizeBrand(pm.brand ?? '')
  return {
    payment_method_id: pm.payment_method_id,
    brand,
    lastFour: pm.last4,
    expiryMonth: String(pm.exp_month).padStart(2, '0'),
    expiryYear:
      String(pm.exp_year).length === 2
        ? `20${pm.exp_year}`
        : String(pm.exp_year),
    isDefault: pm.is_default,
  }
}

export const createSetupIntent = async (
  patientId: string,
  clinicId: string,
  paymentMethodTypes?: string[],
): Promise<CreateSetupIntentResponse> => {
  const response = await axios.post<CreateSetupIntentResponse>(
    `${BUSINESS_LAYER_API}/api/v1/payment-methods/setup-intent`,
    {
      patient_id: patientId,
      clinic_id: clinicId,
      ...(paymentMethodTypes && paymentMethodTypes.length > 0
        ? { payment_method_types: paymentMethodTypes }
        : {}),
    },
  )
  return response.data
}

export const confirmSavedPaymentMethod = async (params: {
  setup_intent_id: string
  patient_id: string
  clinic_id: string
  is_default?: boolean
}): Promise<ConfirmSavedPaymentMethodResponse> => {
  const response = await axios.post<ConfirmSavedPaymentMethodResponse>(
    `${BUSINESS_LAYER_API}/api/v1/payment-methods/confirm-saved-payment-method`,
    params,
  )
  return response.data
}

export const listPaymentMethods = async (
  patientId: string,
  clinicId: string,
): Promise<ListPaymentMethodsResponse> => {
  const response = await axios.get<ListPaymentMethodsResponse>(
    `${BUSINESS_LAYER_API}/api/v1/payment-methods`,
    { params: { patient_id: patientId, clinic_id: clinicId } },
  )
  return response.data
}

export const deletePaymentMethod = async (
  id: string,
): Promise<DeletePaymentMethodResponse> => {
  const response = await axios.delete<DeletePaymentMethodResponse>(
    `${BUSINESS_LAYER_API}/api/v1/payment-methods/${id}`,
  )
  return response.data
}

export const setDefaultPaymentMethod = async (
  id: string,
  patientId: string,
  clinicId: string,
): Promise<SetDefaultPaymentMethodResponse> => {
  const response = await axios.put<SetDefaultPaymentMethodResponse>(
    `${BUSINESS_LAYER_API}/api/v1/payment-methods/${id}/default`,
    { patient_id: patientId, clinic_id: clinicId },
  )
  return response.data
}
